import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { UserPlusIcon, CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CreateUserModal from './CreateUserModal';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiPost } from '../utils/api';
import { forceReloadUsers } from '../data/users';
import { setInitialUserAttributes } from '../services/userAttributesService';
import { useRefresh } from './RefreshContext';

export default function AddUserButton({ userType }) {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { triggerRefresh } = useRefresh();
  
  // Auto-hide success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Auto-hide error toast after 8 seconds
  useEffect(() => {
    if (showErrorToast && error) {
      const timer = setTimeout(() => {
        setShowErrorToast(false);
        setTimeout(() => {
          setError(null);
        }, 300);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showErrorToast, error]);

  const handleAddUser = () => {
    setShowModal(true);
    setError(null);
    setShowErrorToast(false);
  };

  const handleCreateUser = async (userData) => {
    if (isLoading) {
      return Promise.reject(new Error('Already creating user'));
    }
    
    try {
      setIsLoading(true);
      setError(null);
      setShowErrorToast(false);
      setSuccessMessage('');
      
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        throw new Error('Please log in again');
      }
      
      const response = await apiPost(
        'coreAgent',
        '/core/agent',
        userData,
        tokens.idToken.toString()
      );
      
      if (response.success) {
        // Set user attributes after successful creation
        try {
          await setInitialUserAttributes(
            userData.username,
            userData.attributes,
            tokens.idToken.toString()
          );
          
          // Only show success and close modal if everything succeeds
          setSuccessMessage(`Created user: ${userData.username}`);
          setShowModal(false);
          
          // Trigger refresh of the agent tree
          try {
            triggerRefresh('agentTree');
          } catch (e) {
            console.warn('Failed to refresh user tree:', e);
          }
        } catch (attrError) {
          console.error('Failed to set user attributes:', attrError);
          const errorMsg = `User ${userData.username} was created but setting currencies failed. Please try setting currencies again. Error: ${attrError.message}`;
          setError(errorMsg);
          setShowErrorToast(true);
          // Don't close modal or show success message when attributes fail
          return Promise.reject(new Error(errorMsg));
        }
      } else {
        throw new Error('Failed to create user');
      }
      
      return response;
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error?.message || 'Failed to create user';
      setError(errorMessage);
      setShowErrorToast(true);
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
    setShowErrorToast(false);
  };

  const handleDismissError = () => {
    setShowErrorToast(false);
    setTimeout(() => {
      setError(null);
    }, 300);
  };

  return (
    <div className="relative">
      {/* The button sits in the fixed header, whose stacking context would otherwise keep the
          modal and toasts underneath the tab bar */}
      {ReactDOM.createPortal(
        <>
          {/* Success Toast */}
          {successMessage && (
            <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-50 flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-200 rounded-lg shadow-lg">
              <CheckCircleIcon className="h-5 w-5" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Error Toast */}
          {showErrorToast && error && (
            <div className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-50 flex items-center justify-between gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded-lg shadow-lg border border-red-200 dark:border-red-800 min-w-[300px]">
              <div className="flex items-center gap-2">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
              <button
                onClick={handleDismissError}
                className="flex-shrink-0 ml-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          <CreateUserModal
            isOpen={showModal}
            onClose={handleCloseModal}
            onSubmit={handleCreateUser}
            error={error}
            isLoading={isLoading}
            userType={userType}
          />
        </>,
        document.body
      )}

      <button
        onClick={handleAddUser}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm shadow-primary-500/30
          hover:bg-primary-600 active:bg-primary-700 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-400
          transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
          focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
        title="Add User"
        aria-label="Add User"
      >
        <UserPlusIcon className="h-5 w-5" />
      </button>
    </div>
  );
} 